import { Injectable, Injector, OnDestroy } from '@angular/core';

import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { deviceService } from '@unpispas/upp-base';
import { LocalCacheStore } from '@unpispas/upp-base';
import { storeService } from '@unpispas/upp-base';
import { WSConnection } from '@unpispas/upp-base';

import { Place } from './model/objects/place';
import { RasppiConnect } from './model/objects/rasppiconnect';
import { Ticket } from './model/objects/ticket';

/********************************/
/* Connection lifecycle (shared) */
/********************************/

/**
 * Shared local-WebSocket lifecycle for the `pispas-connect` transports used
 * by {@link wsstoreService} and {@link wsloggerService}: open/reuse via
 * `RasppiConnect.Open(devices, { local: true })`, generation-guarded
 * teardown, reconnect while a connect target keeps resolving, and a
 * `Ready$` transport-connected flag.
 *
 * Also binds a `place`/session-device watch (`OnRefresh`,
 * `OnChildChanged('connects')`) so the transport reconciles on its own when
 * the resolved connect target changes after `SetPlace` (typical: session
 * connect hydrated later from cache/sync) — subclasses do not need to
 * re-implement this watch.
 *
 * Subclasses provide the domain-specific pieces via the abstract/overridable
 * hooks: which connect to use right now ({@link _ResolveConnect}), what to do
 * right after a socket opens or right before it closes
 * ({@link _OnConnectionOpened} / {@link _OnConnectionClosing}), and the log
 * tag / not-connected log level ({@link _LogTag} / {@link _LogNotConnected}).
 */
@Injectable()
abstract class WSConnectBase implements OnDestroy {
    /** Reconnect interval while a connect target keeps resolving (ms). */
    static readonly RECONNECT_MS = 20000;

    protected _place: Place | null = null;
    private _connect: RasppiConnect | null = null;
    /** Currently open connection, if any. Exposed for `_EnsureConnection`-style on-demand callers. */
    protected _connection: WSConnection | null = null;
    private _conn_subscription: Subscription | null = null;
    private _place_subscription: Subscription | null = null;
    private _reconnect_timer: ReturnType<typeof setTimeout> | null = null;
    private _connect_generation = 0;
    /** Last connect resolved by {@link _ResolveConnect}; drives the reconcile debounce. */
    private _last_connect: RasppiConnect | null = null;

    private _ready = new BehaviorSubject<boolean>(false);
    /** Emits whether the local transport is connected and usable. */
    readonly Ready$: Observable<boolean> = this._ready.asObservable();

    /**
     * @param devices Opens local WebSocket connections for the transport.
     */
    protected constructor(protected devices: deviceService) {
        // nothing to do
    }

    /**
     * Tears down connection, timers, place watch and readiness subject.
     */
    ngOnDestroy(): void {
        this._UnbindPlace();
        this._CancelReconnect();
        this._CloseConnection(false);
        this._ready.complete();
    }

    /**
     * Whether the local transport is currently connected.
     */
    get IsReady(): boolean {
        return this._ready.value;
    }

    /**
     * Binds connection lifecycle to `place`. Opens a local connection when
     * {@link _ResolveConnect} resolves one; otherwise closes. Re-opens or
     * re-closes automatically if the resolved connect changes later (place
     * refresh, session device `connects` change).
     * @param place Active place, or null to tear down.
     */
    async SetPlace(place: Place | null): Promise<void> {
        this._BindPlace(place);
        await this._SyncTransport();
    }

    /********************************/
    /* Hooks for derived services   */
    /********************************/

    /**
     * Resolves the connect target to use right now, or null when the
     * transport should stay closed for the given place.
     */
    protected abstract _ResolveConnect(place: Place | null): RasppiConnect | null;

    /** Log tag used in console output (`[WSSTORE]`, `[WSLOGGER]`, …). */
    protected abstract get _LogTag(): string;

    /**
     * Called right after a socket opens successfully, before `Ready$` flips
     * to true. Return false to reject the connection (closes it and
     * schedules a reconnect instead) — e.g. when a required companion
     * resource could not be built from the connection.
     */
    protected _OnConnectionOpened(_connection: WSConnection): boolean {
        void _connection;
        return true;
    }

    /** Called right before an open connection is torn down or lost. */
    protected _OnConnectionClosing(): void {
        // nothing to do by default
    }

    /** Logs the "required but not connected" case; level is domain-specific. */
    protected _LogNotConnected(): void {
        console.warn(this._LogTag + ' Local service required but not connected');
    }

    /********************************/
    /* Private — place watch        */
    /********************************/

    /**
     * Watches place/device updates so the transport reconciles when the
     * resolved connect target changes only after the initial `SetPlace`
     * (cache/sync hydration of `RASPPICONNECT`), not just at call time.
     */
    private _BindPlace(place: Place | null): void {
        this._UnbindPlace();
        this._place = place;

        if (!place) {
            return;
        }

        const _subs = new Subscription();
        _subs.add(place.OnRefresh.subscribe(() => {
            this._ReconcileTransport();
        }));

        const _device = place.data.session?.device;
        if (_device) {
            _subs.add(_device.OnChildChanged.subscribe((relation) => {
                if (relation === 'connects') {
                    this._ReconcileTransport();
                }
            }));
            _subs.add(_device.OnRefresh.subscribe(() => {
                this._ReconcileTransport();
            }));
        }

        this._place_subscription = _subs;
    }

    private _UnbindPlace(): void {
        this._place_subscription?.unsubscribe();
        this._place_subscription = null;
    }

    /**
     * Re-syncs the transport only when the resolved connect target actually
     * changed since the last sync — avoids flapping the socket on every
     * unrelated place/device refresh tick.
     */
    private _ReconcileTransport(): void {
        if (this._place == null) {
            return;
        }

        if (this._ResolveConnect(this._place) === this._last_connect) {
            return;
        }

        void this._SyncTransport();
    }

    /********************************/
    /* Private — connection         */
    /********************************/

    private async _SyncTransport(): Promise<void> {
        this._CancelReconnect();
        await this._OpenConnection();
    }

    protected async _OpenConnection(): Promise<void> {
        const _connect = this._ResolveConnect(this._place);
        this._last_connect = _connect;

        if (!_connect) {
            this._CloseConnection(true);
            return;
        }

        if (!_connect.EndPoint) {
            console.warn(this._LogTag + ' Local connect required but connect URI is not parseable');
            this._CloseConnection(true);
            this._ScheduleReconnect();
            return;
        }

        // Drop readiness before tear-down so a successful reopen re-emits Ready$=true
        // (BehaviorSubject would otherwise skip an already-true → true transition).
        if (this._ready.value) {
            this._EmitReady(false);
        }

        this._TearDownConnection();
        const _generation = ++this._connect_generation;

        let _connection: WSConnection | null = null;
        try {
            _connection = await _connect.Open(this.devices, { local: true });
        }
        catch (error: unknown) {
            console.warn(this._LogTag + ' Local Connect threw', error);
            _connection = null;
        }

        if (_generation !== this._connect_generation) {
            _connect.Close(_connection);
            return;
        }

        if (!_connection) {
            this._LogNotConnected();
            this._EmitReady(false);
            this._ScheduleReconnect();
            return;
        }

        if (!this._OnConnectionOpened(_connection)) {
            _connect.Close(_connection);
            this._EmitReady(false);
            this._ScheduleReconnect();
            return;
        }

        this._connect = _connect;
        this._connection = _connection;
        this._conn_subscription = _connection.OnDisconnected.subscribe(() => {
            this._OnTransportLost();
        });

        console.info(this._LogTag + ' Local transport connected');
        this._EmitReady(true);
    }

    private _OnTransportLost(): void {
        this._conn_subscription?.unsubscribe();
        this._conn_subscription = null;
        this._connect = null;
        this._connection = null;
        this._OnConnectionClosing();
        this._EmitReady(false);

        if (this._ResolveConnect(this._place)) {
            this._ScheduleReconnect();
        }
    }

    /**
     * Cancels any in-flight open and tears down the active socket.
     * @param emitReady Whether to publish Ready$=false.
     */
    private _CloseConnection(emitReady: boolean): void {
        this._connect_generation++;
        this._TearDownConnection();

        if (emitReady) {
            this._EmitReady(false);
        }
    }

    private _TearDownConnection(): void {
        this._conn_subscription?.unsubscribe();
        this._conn_subscription = null;
        this._OnConnectionClosing();

        if (this._connection) {
            const _connection = this._connection;
            const _connect = this._connect;
            this._connection = null;
            this._connect = null;

            if (_connect) {
                _connect.Close(_connection);
            }
            else {
                // Should not happen (_connect and _connection are always set together);
                // fall back to a direct Disconnect so the socket never leaks.
                _connection.Disconnect();
            }
        }
    }

    private _ScheduleReconnect(): void {
        this._CancelReconnect();
        if (!this._ResolveConnect(this._place)) {
            return;
        }

        this._reconnect_timer = setTimeout(() => {
            this._reconnect_timer = null;
            void this._OpenConnection();
        }, WSConnectBase.RECONNECT_MS);
    }

    private _CancelReconnect(): void {
        if (this._reconnect_timer != null) {
            clearTimeout(this._reconnect_timer);
            this._reconnect_timer = null;
        }
    }

    private _EmitReady(ready: boolean): void {
        if (this._ready.value === ready) {
            return;
        }

        this._ready.next(ready);
    }
}

/********************************/
/* wsstore — dual-write store    */
/********************************/

/**
 * Dual-write store for pending sync packages: browser `storeService` as primary
 * path, optional local print-service cache (`LocalCacheStore`) as backup.
 *
 * Reads the session connect from `Place` directly (not `dataService.alives` /
 * `RasppiView`) and opens/closes its socket via `RasppiConnect.Open`/`Close` —
 * the same path used by `RasppiView.WSOpen`/`WSClose`. Connection lifecycle
 * and the `place`/device watch that follows late `RequiresLocalBackup` flips
 * are inherited from {@link WSConnectBase}. Emits transport readiness only;
 * domain flags such as `Place.BackupReady` stay in `Syncronizator`.
 */
@Injectable({
    providedIn: 'root'
})
export class wsstoreService extends WSConnectBase implements OnDestroy {
    private _cache: LocalCacheStore | null = null;

    /**
     * @param store Primary browser persistence.
     * @param devices Opens local WebSocket connections for the cache module.
     */
    constructor(private store: storeService, devices: deviceService) {
        super(devices);
    }

    /**
     * Reads from browser storage (primary path). Never reads the backup here.
     * @param key Storage key.
     * @returns Stored value, or null when missing.
     */
    async Get(key: string): Promise<any | null> {
        return await this.store.Get(key);
    }

    /**
     * Writes to browser storage first, then fire-and-forget replica to backup
     * when connected. Backup failures do not reverse the primary write.
     * @param key Storage key.
     * @param value Value to persist (JSON-serializable for backup).
     */
    async Set(key: string, value: any): Promise<void> {
        await this.store.Set(key, value);

        const _cache = this._cache;
        if (!_cache) {
            return;
        }

        void _cache.Set(key, JSON.stringify(value)).catch((error: unknown) => {
            console.warn('[WSSTORE] Backup Set failed for key "' + key + '"', error);
        });
    }

    /**
     * Deletes from browser storage first, then fire-and-forget backup delete.
     * @param key Storage key.
     */
    async Del(key: string): Promise<void> {
        await this.store.Del(key);

        const _cache = this._cache;
        if (!_cache) {
            return;
        }

        void _cache.Del(key).catch((error: unknown) => {
            console.warn('[WSSTORE] Backup Del failed for key "' + key + '"', error);
        });
    }

    /**
     * Reads only from the local backup cache. Used by sync recovery, not by
     * the normal read path.
     * @param key Storage key.
     * @returns Parsed value, or null when disconnected / missing / invalid.
     */
    async GetBackup(key: string): Promise<any | null> {
        if (!this._cache) {
            return null;
        }

        try {
            const _raw = await this._cache.Get(key);
            if (_raw == null) {
                return null;
            }

            return JSON.parse(_raw);
        }
        catch (error: unknown) {
            console.warn('[WSSTORE] Backup Get failed for key "' + key + '"', error);
            return null;
        }
    }

    protected _ResolveConnect(place: Place | null): RasppiConnect | null {
        if (!place?.RequiresLocalBackup) {
            return null;
        }

        return place.data.session?.device?.connect ?? null;
    }

    protected get _LogTag(): string {
        return '[WSSTORE]';
    }

    protected override _OnConnectionOpened(connection: WSConnection): boolean {
        const _cache = this.devices.WSCacheStore(connection);
        if (!_cache) {
            return false;
        }

        this._cache = _cache;
        return true;
    }

    protected override _OnConnectionClosing(): void {
        this._cache = null;
    }
}

/********************************/
/* wslogger — types              */
/********************************/

/** Closed action vocabulary accepted by the local activitylog service. */
export type LocalActivityLogAction =
    | 'CREATED'
    | 'UPDATED'
    | 'PAYMENT'
    | 'CANCELLED'
    | 'REFUND'
    | 'RECEIPT';

/** Event codes in scope for the local activity log. */
export type LocalActivityLogEvent = 'e0' | 'e5';

/**
 * APPEND payload fields (without `seq` / `hash` / `prev` — computed by the
 * local runtime).
 */
export interface LocalActivityLogEntry {
    ts: string;
    event: LocalActivityLogEvent;
    action: LocalActivityLogAction;
    uuid: string;
    objid: number | null;
    series: string | null;
    invceno: string | null;
    status: string | null;
    price: number | null;
    refund: number | null;
    payment: string | null;
    session: string | null;
    device: string | null;
    custom: unknown;
}

/** Ticket fields used to derive CREATED / UPDATED / PAYMENT / … */
type TicketActivitySnapshot = {
    uuid: string;
    series: string | null;
    invceno: string | null;
    status: string | null;
    price: number | null;
    refund: number | null;
    payment: string | null;
};

/**
 * `{header, reglog}` shape returned by `QueryLocalActivity`, matching the
 * columns `upp-report-activity` already reads from remote `place/ticketlog`
 * (`reglog` rows are string arrays aligned to `header`, not objects).
 */
export interface LocalActivityLogQueryResult {
    header: string[];
    reglog: string[][];
}

/** One raw row as returned by the Rust `QUERY` wire response. */
type LocalActivityLogQueryRow = Record<string, unknown>;

/** One entry pending delivery to the local activitylog module. */
interface LocalActivityLogQueueItem {
    id: string;
    place: number;
    entry: LocalActivityLogEntry;
}

/********************************/
/* wslogger — local activity log */
/********************************/

/**
 * Fire-and-forget writer for the local `activitylog` module on
 * `pispas-connect`. Opens/reuses a local WebSocket via
 * `RasppiConnect.Open(..., { local: true })`. Never blocks ticket or print
 * flows; pending `APPEND` entries are held in a browser-persisted delivery
 * outbox until the transport acknowledges them. Connection lifecycle and the
 * `place`/device watch are inherited from {@link WSConnectBase}.
 */
@Injectable({
    providedIn: 'root'
})
export class wsloggerService extends WSConnectBase implements OnDestroy {
    /**
     * Column order returned by `QueryLocalActivity`, aligned with the field
     * names `upp-report-activity` already reads from remote `place/ticketlog`
     * (`date`, `action`, `hshchk`, …) so the report reuses its parser as-is.
     */
    private static readonly QUERY_HEADER: readonly string[] = [
        'date', 'event', 'action', 'uuid', 'objid', 'series', 'invceno',
        'status', 'price', 'refund', 'payment', 'session', 'device',
        'custom', 'hash', 'prev', 'hshchk', 'sealchk'
    ];

    /**
     * Browser storage key for the FIFO delivery outbox of pending `APPEND`
     * entries (`LocalActivityLogQueueItem[]`). Not a queryable activity log.
     */
    private static readonly QUEUE_STORAGE_KEY = 'upp-stored-activitylog';

    /**
     * Delay before retrying drain after a failed `APPEND` or a failed outbox
     * hydration while the transport stays ready (no Ready$ false→true edge
     * to re-trigger).
     */
    private static readonly DRAIN_RETRY_MS = 5000;

    /**
     * Consecutive `APPEND` protocol rejects (`exec !== SUCCESS`) for the same
     * outbox head before discarding it as a poison payload.
     */
    private static readonly MAX_APPEND_REJECTS = 3;

    /** Last logged ticket snapshot per `_uuid` (in-session action diff). */
    private readonly _lastByUuid = new Map<string, TicketActivitySnapshot>();

    /** In-memory FIFO of pending `APPEND` entries (mirrors browser storage). */
    private _queue: LocalActivityLogQueueItem[] = [];

    /** Whether {@link _LoadQueue} has already hydrated `_queue` this session. */
    private _queueLoaded = false;

    /**
     * Promise chain serializing in-memory queue mutations and their storage
     * writes so a drain `Del` cannot race an enqueue `Set` on the same key.
     */
    private _queueLock: Promise<void> = Promise.resolve();

    /** Guards against concurrent {@link _Drain} loops. */
    private _draining = false;

    /**
     * Set when {@link _Drain} is requested while another drain is in flight;
     * the active drain re-enters once after releasing the guard.
     */
    private _drainRequested = false;

    /** Scheduled retry after a drain failure while still ready. */
    private _drainRetryTimer: ReturnType<typeof setTimeout> | null = null;

    /** Drains the outbox when the local transport becomes ready. */
    private _readySubscription: Subscription | null = null;

    /**
     * Consecutive protocol rejects per outbox item id. Session-scoped only
     * (not written to the outbox); cleared on successful ACK or discard.
     */
    private readonly _appendRejectCounts = new Map<string, number>();

    /**
     * @param devices Opens local WebSocket connections for `activitylog`.
     * @param store Browser persistence for the delivery outbox.
     */
    constructor(devices: deviceService, private store: storeService) {
        super(devices);
        this._readySubscription = this.Ready$.subscribe((ready) => {
            if (ready) {
                // Load persisted outbox before drain so a prior-session backlog
                // flushes on reconnect without waiting for a new APPEND.
                this._CancelDrainRetry();
                void this._Drain();
            }
        });
    }

    /**
     * Tears down drain retry + `Ready$` subscription, then the shared transport.
     */
    override ngOnDestroy(): void {
        this._CancelDrainRetry();
        this._readySubscription?.unsubscribe();
        this._readySubscription = null;
        super.ngOnDestroy();
    }

    /**
     * Enqueues one `APPEND` entry (persists before network) and drains when
     * the local transport is ready. Never throws to the caller.
     * @param place Place that owns the day file (`place` field on the wire).
     * @param entry APPEND body without chain fields.
     */
    AppendTicketActivity(place: Place, entry: LocalActivityLogEntry): void {
        void this._AppendTicketActivity(place, entry);
    }

    /**
     * Derives the ticket lifecycle action from the previous local snapshot,
     * then enqueues an `APPEND` with `event: 'e0'`. Skips when the action is
     * unchanged (`NOCHANGES`); when local connect is down the entry stays in
     * the delivery outbox until drain succeeds.
     * @param place Place for the wire `place` id.
     * @param ticket Ticket after a successful commit.
     */
    LogTicketCommit(place: Place, ticket: Ticket): void {
        const _snapshot = this._Snapshot(ticket);
        const _last = this._lastByUuid.get(_snapshot.uuid) ?? null;
        const _action = this._DeriveAction(_last, _snapshot);
        if (!_action) {
            return;
        }

        this._lastByUuid.set(_snapshot.uuid, _snapshot);
        this.AppendTicketActivity(place, this._BuildEntry(place, ticket, 'e0', _action, null));
    }

    /**
     * Fire-and-forget `APPEND` for a receipt print (`event: 'e5'`,
     * `action: 'RECEIPT'`) with opaque `custom` payload.
     * @param place Place for the wire `place` id.
     * @param ticket Ticket being printed (identity / fiscal fields).
     * @param info Opaque printed representation (same as remote RECEIPT).
     */
    LogReceipt(place: Place, ticket: Ticket, info: unknown): void {
        this.AppendTicketActivity(place, this._BuildEntry(place, ticket, 'e5', 'RECEIPT', info));
    }

    /**
     * Queries the local activity log for `place` between `ini` and `end`
     * (inclusive), translating the Rust `{header, reglog}` response into the
     * same `{header, reglog}` shape already consumed from remote
     * `place/ticketlog`. Returns null when the local transport is
     * unavailable or the request fails — callers must treat null as
     * "source not available", never as "zero rows in range".
     * @param place Place that owns the day file (`place` field on the wire).
     * @param ini Range start (inclusive).
     * @param end Range end (inclusive).
     */
    async QueryLocalActivity(place: Place, ini: Date, end: Date): Promise<LocalActivityLogQueryResult | null> {
        if (!place?.objid) {
            console.debug('[WSLOGGER] Place has no objid; skip QUERY');
            return null;
        }

        const _placeId = Number(place.objid);
        if (!Number.isFinite(_placeId)) {
            console.debug('[WSLOGGER] Place objid is not numeric; skip QUERY');
            return null;
        }

        await this._EnsurePlace(place);
        const _connection = await this._EnsureConnection();
        if (!_connection) {
            console.debug('[WSLOGGER] Local service unavailable; skip QUERY');
            return null;
        }

        try {
            const _response = await _connection.Send('activitylog', {
                command: 'QUERY',
                place: _placeId,
                ini: ini.toISOString(),
                end: end.toISOString()
            });

            if (!_response || _response.exec != 'SUCCESS') {
                console.warn('[WSLOGGER] QUERY rejected', _response?.data ?? _response);
                return null;
            }

            const _reglog = _response.data?.reglog;
            if (!Array.isArray(_reglog)) {
                console.warn('[WSLOGGER] QUERY response missing reglog', _response.data);
                return null;
            }

            return {
                header: wsloggerService.QUERY_HEADER.slice(),
                reglog: (_reglog as LocalActivityLogQueryRow[]).map((row) => this._ToReportRow(row))
            };
        }
        catch (error: unknown) {
            console.warn('[WSLOGGER] QUERY failed', error);
            return null;
        }
    }

    protected _ResolveConnect(place: Place | null): RasppiConnect | null {
        const _connect = place?.data.session?.device?.connect ?? null;
        return _connect?.IsLocal ? _connect : null;
    }

    protected get _LogTag(): string {
        return '[WSLOGGER]';
    }

    protected override _LogNotConnected(): void {
        console.debug('[WSLOGGER] Local service required but not connected');
    }

    /********************************/
    /* Private — query               */
    /********************************/

    private _ToReportRow(row: LocalActivityLogQueryRow): string[] {
        return [
            this._ToRegstrDate(row['ts']),
            this._ToTextField(row['event']),
            this._ToTextField(row['action']),
            this._ToTextField(row['uuid']),
            this._ToTextField(row['objid']),
            this._ToTextField(row['series']),
            this._ToTextField(row['invceno']),
            this._ToTextField(row['status']),
            this._ToTextField(row['price']),
            this._ToTextField(row['refund']),
            this._ToTextField(row['payment']),
            this._ToTextField(row['session']),
            this._ToTextField(row['device']),
            this._ToCustomField(row['custom']),
            this._ToTextField(row['hash']),
            this._ToTextField(row['prev']),
            this._ToFlagField(row['hshchk']),
            this._ToFlagField(row['sealchk'])
        ];
    }

    /**
     * ISO-8601 `ts` (`2026-07-31T10:15:23.451Z`) to the `yyyy-mm-dd hh:mm:ss`
     * shape the report's `ToLocalDateText` already parses for the remote
     * source. Both are UTC wall-clock values, so this is a reformat, not a
     * timezone conversion.
     */
    private _ToRegstrDate(value: unknown): string {
        const _ts = typeof value === 'string' ? value : '';
        const _match = _ts.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})/);
        return _match ? (_match[1] + ' ' + _match[2]) : '';
    }

    private _ToTextField(value: unknown): string {
        return value == null ? '' : String(value);
    }

    private _ToCustomField(value: unknown): string {
        if (value == null) {
            return '';
        }
        return typeof value === 'string' ? value : JSON.stringify(value);
    }

    /**
     * Boolean chain/seal flags travel as `'1'`/`'0'`, matching the remote
     * `hshchk` convention that `getRowClass` in the activity report already
     * checks against (`row['hshchk'] === '0'`).
     */
    private _ToFlagField(value: unknown): string {
        if (value == null) {
            return '';
        }
        return value ? '1' : '0';
    }

    /********************************/
    /* Private — outbox             */
    /********************************/

    /**
     * Runs `fn` exclusively against the in-memory outbox and its storage
     * mirror. Network `Send` stays outside this lock.
     */
    private _WithQueueLock<T>(fn: () => Promise<T>): Promise<T> {
        const _run = this._queueLock.then(fn, fn);
        this._queueLock = _run.then(
            () => undefined,
            () => undefined
        );
        return _run;
    }

    /**
     * Enqueues `entry` in the delivery outbox (persist first), then kicks a
     * drain attempt. Never throws to the caller.
     */
    private async _AppendTicketActivity(place: Place, entry: LocalActivityLogEntry): Promise<void> {
        try {
            if (!place?.objid) {
                console.debug('[WSLOGGER] Place has no objid; skip APPEND');
                return;
            }

            const _placeId = Number(place.objid);
            if (!Number.isFinite(_placeId)) {
                console.debug('[WSLOGGER] Place objid is not numeric; skip APPEND');
                return;
            }

            await this._EnsurePlace(place);

            const _item: LocalActivityLogQueueItem = {
                id: this._NewQueueItemId(),
                place: _placeId,
                entry
            };

            await this._WithQueueLock(async () => {
                await this._LoadQueue();
                this._queue.push(_item);
                await this._PersistQueue();
            });
            void this._Drain();
        }
        catch (error: unknown) {
            console.warn('[WSLOGGER] APPEND enqueue failed', error);
        }
    }

    /**
     * Hydrates `_queue` from browser storage once per successful load.
     * Corrupt storage or individual items are discarded with a warning.
     * A failed `Get` leaves the queue unloaded so a later drain can retry.
     */
    private async _LoadQueue(): Promise<void> {
        if (this._queueLoaded) {
            return;
        }

        try {
            const _raw = await this.store.Get(wsloggerService.QUEUE_STORAGE_KEY);
            if (_raw == null) {
                this._queue = [];
                this._queueLoaded = true;
                return;
            }

            if (!Array.isArray(_raw)) {
                console.warn('[WSLOGGER] Outbox storage is not an array; discarding', _raw);
                this._queue = [];
                this._queueLoaded = true;
                return;
            }

            this._queue = [];
            for (const _item of _raw) {
                if (!this._IsQueueItem(_item)) {
                    console.warn('[WSLOGGER] Discarding corrupt outbox item', _item);
                    continue;
                }
                this._queue.push(_item);
            }
            this._queueLoaded = true;
        }
        catch (error: unknown) {
            console.warn('[WSLOGGER] Failed to load outbox', error);
            throw error;
        }
    }

    /**
     * Writes the current `_queue` snapshot to browser storage, or deletes the
     * key when empty. Must run under {@link _WithQueueLock}.
     */
    private async _PersistQueue(): Promise<void> {
        if (this._queue.length > 0) {
            await this.store.Set(wsloggerService.QUEUE_STORAGE_KEY, this._queue.slice());
            return;
        }

        await this.store.Del(wsloggerService.QUEUE_STORAGE_KEY);
    }

    /**
     * Loads the outbox if needed, then sends queued `APPEND` entries in FIFO
     * order while the local transport is ready. Stops on the first transport
     * failure or protocol reject below the discard threshold, leaving that
     * item at the head, and schedules a retry while still ready. Repeated
     * protocol rejects for the same head discard that item so a poison
     * payload cannot block the queue. Queue mutations / storage writes are
     * serialized via {@link _WithQueueLock}.
     */
    private async _Drain(): Promise<void> {
        if (this._draining) {
            this._drainRequested = true;
            return;
        }

        this._draining = true;
        let _failed = false;
        try {
            do {
                this._drainRequested = false;

                await this._WithQueueLock(async () => {
                    await this._LoadQueue();
                });

                while (true) {
                    const _item = await this._WithQueueLock(async () => {
                        return this._queue[0] ?? null;
                    });
                    if (!_item) {
                        break;
                    }

                    const _connection = await this._EnsureConnection();
                    if (!_connection) {
                        break;
                    }

                    try {
                        const _response = await _connection.Send('activitylog', {
                            command: 'APPEND',
                            place: _item.place,
                            entry: _item.entry
                        });

                        if (!_response || _response.exec != 'SUCCESS') {
                            console.warn('[WSLOGGER] APPEND rejected', _response?.data ?? _response);
                            const _discarded = await this._OnAppendRejected(_item);
                            if (_discarded) {
                                continue;
                            }
                            _failed = true;
                            break;
                        }

                        this._appendRejectCounts.delete(_item.id);
                        await this._WithQueueLock(async () => {
                            if (this._queue[0]?.id === _item.id) {
                                this._queue.shift();
                                await this._PersistQueue();
                            }
                        });
                    }
                    catch (error: unknown) {
                        console.warn('[WSLOGGER] APPEND failed', error);
                        _failed = true;
                        break;
                    }
                }
            } while (this._drainRequested && !_failed);
        }
        catch (error: unknown) {
            console.warn('[WSLOGGER] Outbox drain aborted', error);
            _failed = true;
        }
        finally {
            this._draining = false;

            // Retry while ready when the in-memory queue still has work, or
            // hydration never completed (prior-session backlog may still be
            // in storage even though `_queue` looks empty).
            if (_failed && this.IsReady && this._NeedsDrainRetry()) {
                this._ScheduleDrainRetry();
            }
            else if (this._drainRequested) {
                this._drainRequested = false;
                void this._Drain();
            }
            else if (this._queueLoaded && this._queue.length === 0) {
                this._CancelDrainRetry();
            }
        }
    }

    /**
     * Counts a protocol reject for `item`. Discards the outbox head once
     * {@link MAX_APPEND_REJECTS} is reached; otherwise leaves it for retry.
     * @returns `true` when the head was discarded.
     */
    private async _OnAppendRejected(item: LocalActivityLogQueueItem): Promise<boolean> {
        const _rejects = (this._appendRejectCounts.get(item.id) ?? 0) + 1;
        if (_rejects < wsloggerService.MAX_APPEND_REJECTS) {
            this._appendRejectCounts.set(item.id, _rejects);
            return false;
        }

        console.warn(
            '[WSLOGGER] Discarding outbox item after repeated APPEND rejects',
            item
        );
        this._appendRejectCounts.delete(item.id);
        await this._WithQueueLock(async () => {
            if (this._queue[0]?.id === item.id) {
                this._queue.shift();
                await this._PersistQueue();
            }
        });
        return true;
    }

    /** Whether a delayed drain retry can still make progress while ready. */
    private _NeedsDrainRetry(): boolean {
        return !this._queueLoaded || this._queue.length > 0;
    }

    /** Schedules a single drain retry while the transport stays ready. */
    private _ScheduleDrainRetry(): void {
        if (this._drainRetryTimer != null) {
            return;
        }

        this._drainRetryTimer = setTimeout(() => {
            this._drainRetryTimer = null;
            if (this.IsReady && this._NeedsDrainRetry()) {
                void this._Drain();
            }
        }, wsloggerService.DRAIN_RETRY_MS);
    }

    private _CancelDrainRetry(): void {
        if (this._drainRetryTimer == null) {
            return;
        }

        clearTimeout(this._drainRetryTimer);
        this._drainRetryTimer = null;
    }

    /** Shape guard for a persisted outbox row (id, place, entry wire fields). */
    private _IsQueueItem(value: unknown): value is LocalActivityLogQueueItem {
        if (value == null || typeof value !== 'object') {
            return false;
        }

        const _item = value as Partial<LocalActivityLogQueueItem>;
        const _entry = _item.entry as Partial<LocalActivityLogEntry> | null | undefined;
        return typeof _item.id === 'string'
            && typeof _item.place === 'number'
            && Number.isFinite(_item.place)
            && _entry != null
            && typeof _entry === 'object'
            && typeof _entry.ts === 'string'
            && typeof _entry.event === 'string'
            && typeof _entry.action === 'string'
            && typeof _entry.uuid === 'string';
    }

    /** Stable unique id for an outbox row (`crypto.randomUUID` when available). */
    private _NewQueueItemId(): string {
        const _crypto = globalThis.crypto as Crypto | undefined;
        if (_crypto && typeof _crypto.randomUUID === 'function') {
            return _crypto.randomUUID();
        }
        return 'alog-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }

    /********************************/
    /* Private — append + derive    */
    /********************************/

    private _BuildEntry(
        place: Place,
        ticket: Ticket,
        event: LocalActivityLogEvent,
        action: LocalActivityLogAction,
        custom: unknown
    ): LocalActivityLogEntry {
        const _objidRaw = ticket.objid != null ? Number(ticket.objid) : NaN;
        const _session = ticket.session?.objid ?? place.data.session?.objid ?? null;
        const _device = place.data.session?.device?.uuid ?? null;

        return {
            ts: new Date().toISOString(),
            event,
            action,
            uuid: String(ticket._uuid || ''),
            objid: Number.isFinite(_objidRaw) ? _objidRaw : null,
            series: ticket.series ?? null,
            invceno: ticket.invceno ?? null,
            status: ticket.status ?? null,
            price: ticket.price != null ? Number(ticket.price) : null,
            refund: ticket.refund != null ? Number(ticket.refund) : null,
            payment: ticket.payment ?? null,
            session: _session != null ? String(_session) : null,
            device: _device != null ? String(_device) : null,
            custom
        };
    }

    private _Snapshot(ticket: Ticket): TicketActivitySnapshot {
        return {
            uuid: String(ticket._uuid || ''),
            series: ticket.series ?? null,
            invceno: ticket.invceno ?? null,
            status: ticket.status ?? null,
            price: ticket.price != null ? Number(ticket.price) : null,
            refund: ticket.refund != null ? Number(ticket.refund) : null,
            payment: ticket.payment ?? null
        };
    }

    /**
     * Ports the v3 / ticketlog status diff, mapped onto the closed local
     * action vocabulary (`CREATED` … `RECEIPT`). Returns null when nothing
     * meaningful changed.
     */
    private _DeriveAction(
        last: TicketActivitySnapshot | null,
        current: TicketActivitySnapshot
    ): LocalActivityLogAction | null {
        const _newStatus = current.status;
        const _newPrice = current.price;
        const _newPayment = current.payment || '';

        if (last == null) {
            switch (_newStatus) {
                case 'DE':
                case 'CC':
                    return 'CANCELLED';
                case 'PD':
                    return 'PAYMENT';
                default:
                    return 'CREATED';
            }
        }

        let _action: LocalActivityLogAction | null = null;
        const _oldStatus = last.status;
        const _oldPrice = last.price;
        const _oldPayment = last.payment || '';

        if (_oldPayment != _newPayment) {
            _action = _newStatus === 'PD' ? 'PAYMENT' : 'UPDATED';
        }

        if (_oldPrice != _newPrice) {
            _action = 'UPDATED';
        }

        if (_oldStatus != _newStatus) {
            switch (_newStatus) {
                case 'AC':
                case 'RD':
                case 'PP':
                    _action = 'UPDATED';
                    break;
                case 'PD':
                    _action = 'PAYMENT';
                    break;
                case 'CC':
                    _action = _newPayment !== '' ? 'REFUND' : 'CANCELLED';
                    break;
                case 'DE':
                    _action = 'CANCELLED';
                    break;
            }
        }

        if ((_newStatus == 'DE') || (_newPrice == 0)) {
            _action = 'CANCELLED';
        }

        return _action;
    }

    /********************************/
    /* Private — connection         */
    /********************************/

    private async _EnsurePlace(place: Place): Promise<void> {
        if (this._place === place) {
            return;
        }

        await this.SetPlace(place);
    }

    private async _EnsureConnection(): Promise<WSConnection | null> {
        if (this._connection && this.IsReady) {
            return this._connection;
        }

        await this._OpenConnection();
        return this._connection;
    }
}

/********************************/
/* Connect facade (single point)*/
/********************************/

/**
 * Single-instance facade over {@link wsstoreService} and {@link wsloggerService}.
 *
 * Each underlying service is resolved lazily — on first access through
 * {@link store} / {@link logger} — rather than at construction time, so a
 * consumer that only ever needs one of the two transports never pays for the
 * other. {@link SetPlace} binds both in one call for callers (like
 * `Syncronizator`) that need to coordinate the two domains together.
 */
@Injectable({ providedIn: 'root' })
export class wsconnectService {
    private _store: wsstoreService | null = null;
    private _logger: wsloggerService | null = null;

    constructor(private injector: Injector) {
        // nothing to do — store/logger are resolved lazily, see getters below
    }

    /** Dual-write store for pending sync packages (resolved on first access). */
    get store(): wsstoreService {
        if (!this._store) {
            this._store = this.injector.get(wsstoreService);
        }
        return this._store;
    }

    /** Local activity-log transport for `pispas-connect` (resolved on first access). */
    get logger(): wsloggerService {
        if (!this._logger) {
            this._logger = this.injector.get(wsloggerService);
        }
        return this._logger;
    }

    /** Binds both local transports to `place` in one call. */
    async SetPlace(place: Place | null): Promise<void> {
        await this.store.SetPlace(place);
        await this.logger.SetPlace(place);
    }
}
